import { useEffect } from 'react'

import { authenticatedApi } from '@/utils/authenticatedApi'
import { applyBrandColor, cacheBrand } from '@/lib/brand'

/**
 * Paints the tenant's brand colour onto the document.
 *
 * Renders nothing. It exists as a component rather than a module-level call so
 * the fetch happens once inside the React tree, after the auth token is in
 * place — a top-level call in main.tsx would fire before sign-in and get a 401
 * on every reload.
 *
 * main.tsx has already painted the CACHED colour on the first frame, so this
 * is the authoritative correction rather than the first paint. That ordering
 * is the point: without the cache the app opens LabBOXX blue and repaints to
 * the lab's colour a few hundred milliseconds later, which reads as a bug.
 *
 * A failed fetch deliberately changes nothing. The cached colour is a better
 * answer than reverting a white-labelled lab to the product default because
 * one request timed out.
 */
export function BrandTheme() {
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const res = await authenticatedApi.get<{ primary_color?: string }>(
        '/v1/core/branding/', { fallbackToMockData: false })
      if (cancelled || res.error || !res.data) return
      const hex = (res.data.primary_color || '').trim()
      applyBrandColor(hex)
      cacheBrand(hex)
    })()
    return () => { cancelled = true }
  }, [])

  return null
}

export default BrandTheme
